import { getAuth, createUserWithEmailAndPassword } from 'firebase/auth';
import { addDoc, collection, doc, getDoc, getFirestore, onSnapshot, setDoc, updateDoc } from 'firebase/firestore';
import { logoutUser } from './AuthActions';
import { FETCH_USER_FAILURE, FETCH_USER_REQUEST, FETCH_USER_SUCCESS, TOGGLE_THEME, UPDATE_USER_FAILURE, UPDATE_USER_REQUEST, UPDATE_USER_SUCCESS } from './Types';
import { message, notification } from 'antd';
import { mapFirebaseAuthError } from '../utils';
import { newTracking } from './AppActions';
import { auth ,db} from '../App';

// Initialize Firebase services

export const toggleTheme = () => ({
  type: TOGGLE_THEME,
});


export const registerUser = (userData) => async (dispatch) => {
  try {
    dispatch({ type: 'REGISTER_USER_REQUEST' });

    // Step 1: Create a new user with email and password (without logging the admin out)
    const { email, password, role, name, time } = userData;
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);

    if (userCredential) {
      // Step 2: Add user details to Firestore (excluding the password)
      const userID = userCredential.user.uid;

      // Step 2: Add user details to Firestore under "users/userID"
      await setDoc(doc(db, 'users', userID), {
        active: true,
        email: email,
        role: role,
        type: 'staff',
        names: name,
        uid: userID,
        time
      });
      notification.success({
        message: `Welcome, ${name}`,
        description: "You have been successfully registered.",
      });
      dispatch({ type: 'REGISTER_USER_SUCCESS', payload: { email, role, name } });

      // Dispatching the newTracking action
      dispatch(newTracking(
        'User',
        `Register User`,
        'completed',
        {
          id: userID,
          data: {
            active: true,
            email: email,
            role: role,
            type: 'staff',
            names: name,
            uid: userID,
            time
          }
        }
      ));

      dispatch(logoutUser());

    }
  } catch (error) {
    message.error(`Failed to register user!`);

    dispatch({
      type: 'REGISTER_USER_FAILURE',
      payload: error,
    });
  }
};




export const masterRegister = (userData) => async (dispatch) => {
  const { role, masterCode } = userData;

  try {
    // Step 1: If the role is "new", fetch the staff code from the masterCodes document
    if (role === "newStaff") {
      const docRef = doc(db, "masterCodes", "registerCodes");
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error("Master Codes document not found.");
      }

      const { staffCode } = docSnap.data();

      // Step 2: Compare the fetched staffCode with the passed masterCode
      if (staffCode !== masterCode) {
        throw new Error("Invalid Master Code. Registration denied.");
      }
    }


    const { email, password, name, time } = userData;
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);

    if (userCredential) {
      // Step 2: Add user details to Firestore (excluding the password)
      const userID = userCredential.user.uid;

      // Step 2: Add user details to Firestore under "users/userID"
      await setDoc(doc(db, 'users', userID), {
        active: true,
        email: email,
        role: 'new',
        type: 'staff',
        names: name,
        uid: userID,
        time
      });


      dispatch({ type: 'REGISTER_USER_SUCCESS', payload: { email, role, name } });

      // Dispatching the newTracking action
      dispatch(newTracking(
        'User',
        `Register User`,
        'completed',
        {
          id: userID,
          data: {
            active: true,
            email: email,
            role: 'new',
            type: 'staff',
            names: name,
            uid: userID,
            time
          }
        }
      ));

      notification.success({
        message: `Welcome, ${name}`,
        description: "You have been successfully registered.",
      });
    }


  } catch (error) {
    // Handle errors
    dispatch({ type: "REGISTER_ERROR", payload: error.message });
    notification.error({
      message: "Registration Error",
      description: error.message === 'Invalid Master Code. Registration denied.' ?
        'Invalid Master Code. Registration denied.' : mapFirebaseAuthError(error.code),
    });
  }
};





// Fetch User Action
export const fetchUser = (userId) => (dispatch) => {
  dispatch({ type: FETCH_USER_REQUEST });

  try {
    const userDocRef = doc(db, "users", userId);
    const unsubscribe = onSnapshot(userDocRef, (snapshot) => {
      if (snapshot.exists()) {
        dispatch({ type: FETCH_USER_SUCCESS, payload: snapshot.data() });
      } else {
        dispatch({
          type: FETCH_USER_FAILURE,
          payload: "User not found",
        });
      }
    });

    // Unsubscribe when the component using this action is unmounted
    return unsubscribe;
  } catch (error) {
    dispatch({
      type: FETCH_USER_FAILURE,
      payload: error.message || "Failed to fetch user",
    });
  }
};

// Update User Action
export const updateUser = (userId, updatedData) => async (dispatch) => {
  dispatch({ type: UPDATE_USER_REQUEST });

  try {
    const userDocRef = doc(db, "users", userId);
    await updateDoc(userDocRef, updatedData);

    dispatch({ type: UPDATE_USER_SUCCESS });

    // Dispatching the newTracking action
    dispatch(newTracking(
      'User',
      `Update User`,
      'completed',
      {
        id: userId,
        data: updatedData
      }
    ));

    message.success(`User profile updated successful!`);

    // dispatch(fetchUser(userId)); // Refresh user data after update
  } catch (error) {
    message.error(`Failed to update user profile!`);

    dispatch({
      type: UPDATE_USER_FAILURE,
      payload: error.message || "Failed to update user",
    });
  }
};




export const fetchUsers = () => async (dispatch) => {
  try {
    dispatch({ type: 'FETCH_USERS_REQUEST' });

    onSnapshot(collection(db, 'users'), (snapshot) => {
      const users = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));


      dispatch({ type: 'FETCH_USERS_SUCCESS', payload: users });
    });
  } catch (error) {
    dispatch({
      type: 'FETCH_USERS_FAILURE',
      payload: error.message,
    });
  }
};

export const updateUserRole = (userId, newRole) => async (dispatch) => {
  dispatch({ type: 'UPDATE_USER_ROLE_REQUEST' });

  try {
    // Step 1: Get a reference to the user's document in Firestore
    const docRef = doc(db, 'users', userId);

    // Step 2: Update the user's role
    await updateDoc(docRef, { role: newRole });

    dispatch({ type: 'UPDATE_USER_ROLE_SUCCESS', payload: { userId, newRole } });

    // Dispatching the newTracking action
    dispatch(newTracking(
      'User',
      `Update User Role`,
      'completed',
      {
        id: userId,
        data: { newRole }
      }
    ));

    message.success(`User role updated successful!`);

  } catch (error) {
    message.error(`Failed to update user role!`);

    dispatch({
      type: 'UPDATE_USER_ROLE_FAILURE',
      payload: error.message || 'Failed to update user role',
    });
  }
};


export const toggleUserStatus = (userId, isActive) => async (dispatch) => {
  try {
    dispatch({ type: 'TOGGLE_USER_STATUS_REQUEST' });

    await updateDoc(doc(db, 'users', userId), { active: isActive });

    dispatch({ type: 'TOGGLE_USER_STATUS_SUCCESS', payload: { userId, isActive } });

    // Dispatching the newTracking action
    dispatch(newTracking(
      'User',
      `Toggle User Status`,
      'completed',
      {
        id: userId,
        data: { isActive }
      }
    ));

    message.success(`User ${isActive ? 'activated' : 'deactivated'} successful!`);

  } catch (error) {
    message.error(`Failed to ${isActive ? 'activate' : 'deactivate'} user!`);

    dispatch({
      type: 'TOGGLE_USER_STATUS_FAILURE',
      payload: error.message,
    });
  }
};

